<template>
    <a
        v-if="data.anchor"
        :id="data.anchor.slug"
        class="anchor"
    ></a>

    <div :class="classBlock">
        <div
            v-if="data.isCollapsible"
            class="block-title"
        >
            <div
                class="block-title-wrapper"
                @click="toggleBlock"
            >
                {{ data.collapsibleTitle }}
            </div>
        </div>

        <div class="components-wrapper">
            <CmsComponents
                :background="data.backgroundColor"
                :data="data.components"
                :has-eager-image="hasEagerImage"
                :product="product"
                :related-products="relatedProducts"
                :page-type="pageType"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type {
    BlockFragment,
    ShopWithConfidenceProductFragment,
} from '@/graphql/api'
import type { ButtonColors } from '@/components/ui/buttons'
import { getColorForBackground } from '@/components/ui/buttons'
import { cmsColorToButtonColor } from '@/components/cms/index'
import { assertNever } from '@/helpers'

import CmsComponents from '@/components/cms/CmsComponents.vue'
import type { Props as RelatedProductProps } from '@/components/rings/RelatedProducts.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const props = defineProps<{
    data: BlockFragment
    hasEagerImage?: boolean
    relatedProducts?: RelatedProductProps
    product?: ShopWithConfidenceProductFragment
    pageType: PageType
}>()

const isFullWidth = computed(() => props.data.width === 'FULL')

const isFlex = computed(() => props.data.display === 'FLEX')

const verticalSpacing = computed(
    (): 'no-spacing' | 'normal-spacing' | 'large-spacing' => {
        const spacing = props.data.verticalSpacing

        switch (spacing) {
            case 'NONE':
                return 'no-spacing'
            case 'NORMAL':
                return 'normal-spacing'
            case 'LARGE':
                return 'large-spacing'
            default:
                return assertNever(spacing, 'normal-spacing')
        }
    }
)

const isOpen = ref(props.data.isOpenByDefault)

const toggleBlock = () => {
    if (props.data.isCollapsible) {
        isOpen.value = !isOpen.value
    }
}

const classBlock = computed(() => ({
    'page-block': true,
    'full-width': isFullWidth.value,
    'background-overflow': props.data.backgroundOverflow,
    'collapsible': props.data.isCollapsible,
    'open': isOpen.value,
    flex: isFlex.value,
    [verticalSpacing.value]: true,
}))

const backgroundColor = computed(
    (): ButtonColors => cmsColorToButtonColor(props.data.backgroundColor)
)

const color = getColorForBackground(
    cmsColorToButtonColor(props.data.backgroundColor)
)
</script>

<style lang="scss" scoped>
.anchor {
    scroll-margin-top: 110px;
}

.page-block {
    &.background-overflow {
        background: v-bind(backgroundColor);
    }

    .components-wrapper {
        background: v-bind(backgroundColor);
        width: 100%;
        max-width: 1440px;
        margin: 0 auto;
    }

    &.full-width {
        .components-wrapper {
            max-width: unset;
        }
    }

    &.flex {
        .components-wrapper {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        }
    }

    &.normal-spacing {
        padding: 30px 0;
    }

    &.large-spacing {
        padding: 60px 0;
    }

    &.collapsible {
        .components-wrapper {
            display: none;
        }

        &.open {
            .components-wrapper {
                display: block;
            }

            .block-title-wrapper {
                &::after {
                    content: $icon-angle-right;
                }
            }
        }
    }

    .block-title-wrapper {
        width: 720px;
        max-width: 100%;
        margin: 0 auto;
        padding: 15px 20px;
        border-top: 1px solid $grey-200;
        font-size: 1.2rem;
        line-height: 1.2em;
        display: flex;
        align-content: center;
        justify-content: space-between;
        cursor: pointer;
        color: v-bind(color);

        &::after {
            font-family: $icon-font;
            content: $icon-angle-right;
            width: 20px;
            height: 20px;
            display: block;
            text-align: center;
            line-height: 20px;
            font-size: 14px;
        }
    }
}
</style>
